import { useState } from 'react';
import { Modal } from '../Modal';
import { UserPlus, Mail, Phone, Building2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useSimulaData } from '../../hooks/useSimulaData';

interface AddOperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddOperatorModal({ isOpen, onClose }: AddOperatorModalProps) {
  const { operators, supervisors, sectors } = useSimulaData();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    role: 'operator' as 'operator' | 'supervisor' | 'quality',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setIsSubmitting(true);

    try {
      const defaultSector = sectors.data[0];
      const defaultSupervisor = supervisors.data[0];

      if (formData.role === 'operator') {
        if (!defaultSector || !defaultSupervisor) {
          throw new Error('Setor/supervisor nao disponivel para criar operador');
        }
        await operators.add({
          name: formData.name,
          email: formData.email,
          sectorId: defaultSector.id,
          supervisorId: defaultSupervisor.id,
        } as any);
      } else if (formData.role === 'supervisor') {
        if (!defaultSector) throw new Error('Setor nao disponivel para criar supervisor');
        await supervisors.add({
          name: formData.name,
          email: formData.email,
          sectorId: defaultSector.id,
        } as any);
      } else {
        await operators.add({
          name: formData.name,
          email: formData.email,
          sectorId: defaultSector?.id || '',
          supervisorId: defaultSupervisor?.id || '',
        } as any);
      }

      toast.success('Usuario adicionado com sucesso!', {
        description: `${formData.name} foi criado no backend real.`,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: '',
        role: 'operator',
      });
      onClose();
    } catch (error) {
      console.error('[AddOperatorModal] erro ao criar:', error);
      toast.error('Falha ao criar usuario.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Adicionar Novo Operador"
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--foreground)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-medium)',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-button)',
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 'var(--font-weight-semibold)',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            {isSubmitting ? 'Adicionando...' : 'Adicionar Operador'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label
            htmlFor="name"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Nome Completo <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <UserPlus
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
              }}
            />
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: João Silva"
              required
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
              }}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Email <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <Mail
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
              }}
            />
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="joao.silva@empresa.com"
              required
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
              }}
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Telefone
          </label>
          <div style={{ position: 'relative' }}>
            <Phone
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
              }}
            />
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(11) 98765-4321"
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
              }}
            />
          </div>
        </div>

        {/* Department */}
        <div>
          <label
            htmlFor="department"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Departamento
          </label>
          <div style={{ position: 'relative' }}>
            <Building2
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
              }}
            />
            <input
              id="department"
              type="text"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="Ex: SAC, Vendas, Suporte"
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
              }}
            />
          </div>
        </div>

        {/* Role */}
        <div>
          <label
            htmlFor="role"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Função <span style={{ color: 'var(--destructive)' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <Shield
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
                pointerEvents: 'none',
              }}
            />
            <select
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              required
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-base)',
                cursor: 'pointer',
              }}
            >
              <option value="operator">Operador</option>
              <option value="supervisor">Supervisor</option>
              <option value="quality">Qualidade</option>
            </select>
          </div>
        </div>

        {/* Info */}
        <div
          style={{
            backgroundColor: 'var(--muted)',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
          }}
        >
          Um email de boas-vindas será enviado com instruções de acesso ao sistema.
        </div>
      </form>
    </Modal>
  );
}
